import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { ProductTypes } from '../../../products/product-types';
import { TradePositionModel } from 'projects/workspace/src/app/trade/models';
import { ActionsReorderingPositionModel, InputDropdownTypeEnum } from '../../../position-card/position-card-models.model';
import { TradeOfferModel, TradeOfferPositionsByProductType } from 'projects/workspace/src/app/trade-offer/models';
import { TradeOfferApiService } from 'projects/workspace/src/app/trade-offer/services/trade-offer-api.service';
import { UIStatesEnum } from '../../../../models';
import { PositionGroupModel, ProductUnitModel, ResponseModel } from 'projects/workspace/src/app/shared/models';
import {
  selectTradeOfferPositions,
  selectTradeOfferPositionsGroups,
  selectTradeOfferState
} from 'projects/workspace/src/app/trade-offer/store/selectors';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { ActionButtonsService } from '../../../../services/action-buttons.service';
import { CommonModalsActionsEnum, ConfirmModalComponent, DangerModalComponent, PositionGroupModalComponent } from '../../modals-common';
import { PositionsActionsType } from '../../../ui-components/table/custom-table.enums';
import {
  CHANGE_PARENT_QUANTITY_MODAL_DATA,
  REMOVE_POSITION_MODAL_DATA,
  REMOVE_POSITION_NECESSARILY_MODAL_DATA,
  REMOVE_POSITION_PARENT_MODAL_DATA
} from '../../modals.contsans';
import { PositionGroupsService, ProductUnitApiService } from 'projects/workspace/src/app/shared/services';
import { PositionGroupingEnum, PositionMediaTypeEnum, PriceOptionEnum } from 'projects/workspace/src/app/shared/enums';
import { PositionMediaFormComponent } from 'projects/workspace/src/app/shared/forms/position-media-form/position-media-form.component';
import { ARBITRARY_POSITION_TYPES_LIST } from 'projects/workspace/src/app/accounting/accounting.constants';
import { TradeOfferListTabsEnum } from 'projects/workspace/src/app/trade-offer/enums';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';

@Component({
  selector: 'rnpl-offer-edit-position-modal',
  templateUrl: './offer-edit-position-modal.component.html',
  styleUrls: ['./offer-edit-position-modal.component.scss']
})
export class OfferEditPositionModalComponent implements OnInit, OnDestroy {

  public activeTab: 'OVERVIEW'|'BUNDLE_PRODUCTS' = 'OVERVIEW';
  public tabs = [
    {
      tabName: 'OVERVIEW',
      label: 'APP.POSITION'
    },
    {
      tabName: 'BUNDLE_PRODUCTS',
      label: 'BUNDLES.PRODUCTS_IN_BUNDLE'
    }
  ];
  public companyProfile: CompanyProfile;
  public productTypes: typeof ProductTypes = ProductTypes;
  public inputDropdownTypeEnum = InputDropdownTypeEnum;
  public mediaTypeEnum = PositionMediaTypeEnum;
  public positionGroupingEnum = PositionGroupingEnum;
  public position: TradePositionModel;
  public allPositions: TradePositionModel[];
  public groupPositions: TradePositionModel[];
  public positionsByGroup: PositionGroupModel[];
  public rowIndex: number;
  public arbitraryPositionTypesList = ARBITRARY_POSITION_TYPES_LIST;
  public isReadOnly: boolean = true;
  public showControls: boolean = true;
  public positionsUpdateRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public positionsByProductType: {[key in ProductTypes]?: any[]};

  public unitsList: string[] = [];
  public unitsListSource: ProductUnitModel[] = [];

  @ViewChild('positionMediaFormComponent', { static: false }) positionMediaFormComponentRef: PositionMediaFormComponent;

  public readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    public dialogRef: MatDialogRef<OfferEditPositionModalComponent>,
    private tradeOfferApiService: TradeOfferApiService,
    private positionGroupsService: PositionGroupsService,
    private readonly store: Store<AppState>,
    public actionButtonsService: ActionButtonsService,
    private readonly dialog: MatDialog,
    private productUnitApiService: ProductUnitApiService,
    @Inject(MAT_DIALOG_DATA) public data: {
      currentOffer: TradeOfferModel;
      position: TradePositionModel,
      groups: PositionGroupModel[],
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => this.companyProfile = companyProfile);
  }

  ngOnInit() {
    this.position = { ...this.data.position };

    this.getProductUnits();
    this.trackTradeOfferStateChanges();
    this.trackPositionsChanges();
    this.trackPositionsGroupsChanges();
  }

  public getProductUnits(): void {
    const productType = this.isArbitrary
      ? this.position.arbitraryProductType
      : this.position.productType;
    this.productUnitApiService.getUnits$(productType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unitsList: ProductUnitModel[]) => {
        this.unitsListSource = unitsList;
        if (!this.position.general && !this.isArbitrary) {
          this.unitsList = [this.position.unitType];
          return;
        } else {
          this.unitsList = unitsList.map(u => u.name);
        }
        this.redrawControls();
      });
  }

  private trackTradeOfferStateChanges(): void {
    this.store.select(selectTradeOfferState)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: UIStatesEnum) => {
        this.isReadOnly = state === UIStatesEnum.VIEW;
      });
  }

  public getRowIndex(position): number {
    let rowIndex: number;
    this.groupPositions.map((pos, index) => {
      if (pos.id === position.id) {
        rowIndex = index;
      }
    });
    return rowIndex;
  }

  public actionListHandler({actionType, row, moveTo, returnData}: ActionsReorderingPositionModel): void {
    if (actionType === PositionsActionsType.INFO) {
      this.actionButtonsService.displaySidebarHandler(row.productId);
      return;
    }
    if (actionType === PositionsActionsType.TO_GROUP) {
      if (!returnData) {
        // create group and move position to it
        this.dialog.open(PositionGroupModalComponent, {
          data: {
            submitRequest$: (groupName: string) => {
              return this.positionGroupsService.createGroup(groupName, 'ofr',  this.data.currentOffer.id)
                .pipe(tap((group: PositionGroupModel) => this.updatePosition('groupId', group.id)));
            }
          },
        });
        return;
      }
      // move to existed group
      this.updatePosition('groupId', returnData.groupId);
      return;
    }
    this.movePosition(row.id, moveTo);
  }

  public displayProductInfo(productId): void {
    this.actionButtonsService.displaySidebarHandler(productId);
  }

  public deletePositionDialog(): void {
    let data = REMOVE_POSITION_MODAL_DATA;

    if (this.position.linked && this.position.linked.related) {
      data = REMOVE_POSITION_PARENT_MODAL_DATA;
    }

    if (this.position.necessarily) {
      data = REMOVE_POSITION_NECESSARILY_MODAL_DATA;
    }

    const dialog = this.dialog.open(DangerModalComponent, { data });

    dialog.afterClosed().subscribe((res) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.deletePositions([this.position.id]);
      }
    });
  }

  public deletePositions(ids: number[]): void {
    this.tradeOfferApiService.deleteTradeOfferPositions(this.data.currentOffer.id, ids)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getCurrentOffer();
        this.dialogRef.close();
      });
  }

  public updatePosition(fieldName, fieldValue, positionId = +this.position.id,  force = false): void {
    if (fieldName === 'quantity' && !force && this.position.linked && this.position.linked.related) {
      this.dialog.open(ConfirmModalComponent, {
        data: CHANGE_PARENT_QUANTITY_MODAL_DATA,
      }).afterClosed()
        .subscribe((res: CommonModalsActionsEnum) => {
          if (res === CommonModalsActionsEnum.CONFIRM) {
            this.updatePosition(fieldName, fieldValue, positionId, true);
          }
        });
      return;
    }

    this.tradeOfferApiService
      .updateTradeOfferPosition(
        positionId,
        { fieldName, fieldValue },
        this.data.currentOffer.id
      )
      .subscribe((response) => {
        this.position = { ...response.data };
        this.getCurrentOffer();
        if (fieldName === 'arbitraryProductType') {
          this.getProductUnits();
        }
      }, () => {
        if (fieldName === 'unitType') {
          this.redrawControls();
        }
      });
  }

  public redrawControls(): void {
    this.showControls = false;
    setTimeout(() => this.showControls = true);
  }

  private getCurrentOffer(): void {
    this.tradeOfferApiService.getTradeOfferById(this.data.currentOffer.id)
      // .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private movePosition(posId: number, moveTo: number): void {
    this.tradeOfferApiService.changePositionOrder(posId, moveTo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.position = { ...data };
        this.getCurrentOffer();
      });
  }

  private trackPositionsChanges(): void {
    if (this.data.currentOffer.preferences.positionGrouping !== PositionGroupingEnum.PRODUCT_TYPE) { return; }

    this.positionsUpdateRequest$.next(true);

    this.store.select(selectTradeOfferPositions)
      .pipe(
        finalize(() => this.positionsUpdateRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((positions: ResponseModel<TradeOfferPositionsByProductType>) => {
        if (positions) {
          this.positionsByProductType = positions.data;
          this.setupPositionsAndGroups();
        }
      });
  }

  private trackPositionsGroupsChanges(): void {
    if (this.data.currentOffer.preferences.positionGrouping !== PositionGroupingEnum.USER_DEFINED) { return; }

    this.positionsUpdateRequest$.next(true);

    this.store.select(selectTradeOfferPositionsGroups)
      .pipe(
        finalize(() => this.positionsUpdateRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe((positionsGroups: PositionGroupModel[]) => {
        this.positionsByGroup = positionsGroups;
        this.setupPositionsAndGroups();
      });
  }

  public setupPositionsAndGroups(): void {
    if (this.data.currentOffer.preferences.positionGrouping === PositionGroupingEnum.PRODUCT_TYPE) {
      this.groupPositions = this.positionsByProductType[this.position.productType];
      this.allPositions = [].concat(Object.values(this.positionsByProductType));
    }
    if (this.data.currentOffer.preferences.positionGrouping === PositionGroupingEnum.USER_DEFINED) {
      this.allPositions = [].concat(...this.positionsByGroup.map(item => item.positions));
      this.groupPositions = this.positionsByGroup
        .find(g => g.positions.some(p => p.id === this.position.id))
        .positions;
    }

    this.rowIndex = this.getRowIndex(this.position);
  }

  public changePosition({position}): void {
    this.position = { ...position };
    this.setupPositionsAndGroups();
    if (this.position.mediaType === PositionMediaTypeEnum.TEXT) {
      this.positionMediaFormComponentRef.resetTextEditorValue(this.position.description);
    }
    this.getCurrentRowIndex();
    this.getProductUnits();
    this.redrawControls();
  }

  public deleteMediaPositionFile(positionId: number): void {
    this.tradeOfferApiService.deleteMediaPositionFile(this.data.currentOffer.id, positionId)
      .subscribe(() => {
        this.getCurrentOffer();
        this.position = {
          ...this.position,
          fileUrl: null
        };
      });
  }

  public updateMediaPositionFile(positionId: number, event: { file: File; width: number; height: number }): void {
    this.tradeOfferApiService.updateMediaPositionFile(this.data.currentOffer.id, positionId, event.file, event.width, event.height)
      .subscribe((groups: PositionGroupModel[]) => {
        this.getCurrentOffer();
        const allPositions = [].concat(...groups.map(item => item.positions));
        this.position = { ...allPositions.find((p) => p.id === this.position.id) };
      }, () => {
        this.positionMediaFormComponentRef.resetImg();
        this.positionMediaFormComponentRef.resetImgInput();
      });
  }

  public getCurrentRowIndex(): void {
    this.rowIndex = this.groupPositions
      .findIndex(pos => pos.order === this.position.order && pos.positionId === this.position.positionId);
  }

  get allowFractionalValues(): boolean {
    if (!this.position.unitType || !this.unitsListSource.length) { return true; }
    const selectedUnit: ProductUnitModel = this.unitsListSource
      .find((itm: ProductUnitModel) => itm.name === this.position.unitType);
    const isFractionalQuantity: boolean = !!(this.position.quantity % 1);
    return (selectedUnit && !isFractionalQuantity)
      ? selectedUnit.allowFractionalValues
      : isFractionalQuantity;
  }

  get isGoods(): boolean {
    return this.position.productType === ProductTypes.GOODS;
  }
  get isServices(): boolean {
    return this.position.productType === ProductTypes.SERVICES;
  }
  get isDigital(): boolean {
    return this.position.productType === ProductTypes.DIGITAL;
  }
  get isBundle(): boolean {
    return this.position.productType === ProductTypes.BUNDLE;
  }
  get isCredit(): boolean {
    return this.position.productType === ProductTypes.CREDIT;
  }
  get isGeneral(): boolean {
    return this.position.general;
  }

  get isArbitrary(): boolean {
    return this.position.productType === ProductTypes.ARBITRARY;
  }

  get priceOptionNet(): boolean {
    return this.data.currentOffer.preferences.priceOption === PriceOptionEnum.NET
      || this.data.currentOffer.properties.smallBusiness;
  }
  get priceOptionGross(): boolean {
    return this.data.currentOffer.preferences.priceOption === PriceOptionEnum.GROSS
      && !this.data.currentOffer.properties.smallBusiness;
  }

  get ofrDraft(): boolean {
    return this.data.currentOffer.status === TradeOfferListTabsEnum.DRAFT;
  }
  get ofrOpen(): boolean {
    return this.data.currentOffer.status === TradeOfferListTabsEnum.OPEN;
  }
  get ofrAccepted(): boolean {
    return this.data.currentOffer.status === TradeOfferListTabsEnum.ACCEPTED;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
